import React, { useCallback, useMemo, useState } from "react";

import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import { useAppState } from "../../../../context/StateContext";
import useYupValidation from "../../../../hooks/useYupValidation";
import { MilestoneType } from "../../../../models/milestone";
import { Office } from "../../../../models/office";
import { Port } from "../../../../models/port";
import { Shipment } from "../../../../models/shipments";
import { ShipmentDetails } from "../../../../util/enums";
import { ValueOf } from "../../../Generic/Estimate/types";
import { MilestoneDates } from "../../../Generic/MilestoneDates";
import { BaseFieldProps, DetailsSectionsProps, ShipmentKey } from "../../../Shipments/types";
import { useBookingContext } from "../../context/Booking";
import { useBookingResourcesContext } from "../../hooks/useResourcesContext";
import { BookingObjects } from "../../util";
import { DetailsConsigneeSection } from "./components/Consignee";
import { DetailsGeneralSection } from "./components/General";
import { DetailsShipmentSection } from "./components/Shipment";

type ShipmentDetailsGeneralProps = {
  data?: Shipment;
  onUpdateShipment: (attribute: string, value: any, noChanges?: boolean) => void;
  onUpdateAllShipment: (value: Shipment) => void;
  viewOnly?: boolean;
  errors?: yup.ValidationError[];
};

export const Details: React.FC<ShipmentDetailsGeneralProps> = (props) => {
  const { data, onUpdateShipment, onUpdateAllShipment, viewOnly, errors } = props;
  const { t: translate } = useTranslation();
  const { isFMorSPVersion } = useAppState();
  const { handleChangeOptions } = useBookingContext();
  const [officeFromLoadingPort, setOfficeFromLoadingPort] = useState<Office>();

  const { fieldHasError, getErrorByField } = useYupValidation(errors || []);
  const purchaseOrders = useMemo(() => {
    let orders: string = "";

    if (data?.purchaseOrders && data?.purchaseOrders.length > 0) {
      const purchaseOrders = data?.purchaseOrders;

      for (const purchaseOrder of purchaseOrders) {
        orders += purchaseOrder?.reference ? `${purchaseOrder?.reference} - ` : "";
      }
      orders = orders.slice(0, -2);

      return orders;
    }

    return "";
  }, [data?.purchaseOrders]);

  const onUpdateShipmentDetails = useCallback(
    (key: ShipmentKey, value: ValueOf<Shipment> | null, noChanges?: boolean) => {
      onUpdateShipment(key, value, noChanges);
      if (handleChangeOptions) handleChangeOptions(BookingObjects.BOOKING);
    },
    []
  );

  const onUpdateShipmentMilestones = useCallback(
    (key: ShipmentKey, value: ValueOf<Shipment> | null, noChanges?: boolean) => {
      onUpdateShipment(key, value, noChanges);
      if (handleChangeOptions) handleChangeOptions(BookingObjects.MILESTONES);
    },
    []
  );

  const {
    customerList,
    officeList,
    notifyPartyList,
    portList,
    loading: loadingResources,
    milestoneDates,
  } = useBookingResourcesContext();

  const loadingResourcesByVersion = isFMorSPVersion ? false : loadingResources;

  const uniqueOffices = useMemo(() => {
    const officeMap: { [key: string]: Office } = {};

    for (const office of officeList) {
      officeMap[office.beCode] = office;
    }

    return Object.values(officeMap);
  }, [officeList]);

  const customerSelected = isFMorSPVersion
    ? data?.consignee
    : customerList.find((warehouse) => Number(data?.consigneeId) === Number(warehouse?.id));

  const updateOfficeBasedOnLoadingPort = (port: Port) => {
    const foundOfficeFromLoadingPort = officeList.find(
      (office) => office?.port?.siteCode === port?.siteCode
    );

    if (foundOfficeFromLoadingPort?.port) {
      setOfficeFromLoadingPort(foundOfficeFromLoadingPort);
      onUpdateShipmentDetails(ShipmentDetails.OFFICE_ID, foundOfficeFromLoadingPort.id);
    } else if (data?.officeId && officeFromLoadingPort) {
      setOfficeFromLoadingPort(undefined);
      onUpdateShipmentDetails(ShipmentDetails.OFFICE_ID, null);
    }
  };

  const officeSelected = officeList.find((office) => Number(data?.officeId) === Number(office.id));

  const notifyPartySelected = isFMorSPVersion
    ? data?.notifyParty
    : notifyPartyList.find((notifyParty) => Number(data?.notifyPartyId) === Number(notifyParty.id));

  const getOfficeInputValue = () => {
    if (isFMorSPVersion) {
      return data?.office?.name;
    }
    if (officeFromLoadingPort)
      return `${officeFromLoadingPort?.name || ""} - ${officeFromLoadingPort?.beCode || ""}`;

    return officeSelected ? `${officeSelected?.name || ""} - ${officeSelected?.beCode || ""}` : "";
  };

  const generateBaseFieldProps = (name: ShipmentDetails) => {
    const fieldProps: BaseFieldProps<ShipmentDetails> = {
      error: fieldHasError(name),
      errorMessage: getErrorByField(name),
      "data-cy": name,
      dataCy: name,
      label: translate(name),
      placeholder: translate(name),
      "aria-label": name,
      disabled: viewOnly,
      name,
    };

    return fieldProps;
  };

  const baseSectionProps: DetailsSectionsProps = {
    generateBaseFieldProps,
    getErrorByField,
    fieldHasError,
    onUpdateShipmentDetails,
    viewOnly,
    data,
  };

  return (
    <div data-cy="shipments-detail-box">
      <Box paddingTop={1}>
        <DetailsGeneralSection {...baseSectionProps} onUpdateAllShipment={onUpdateAllShipment} />
        <DetailsConsigneeSection
          {...baseSectionProps}
          onUpdateAllShipment={onUpdateAllShipment}
          loadingResourcesByVersion={loadingResourcesByVersion}
          customerSelected={customerSelected}
          customerList={customerList}
        />
        <DetailsShipmentSection
          {...baseSectionProps}
          onUpdateAllShipment={onUpdateAllShipment}
          purchaseOrders={purchaseOrders}
          loadingResourcesByVersion={loadingResourcesByVersion}
          getOfficeInputValue={getOfficeInputValue}
          officeFromLoadingPort={officeFromLoadingPort}
          notifyPartySelected={notifyPartySelected}
          officeList={officeList}
          notifyPartyList={notifyPartyList}
          uniqueOffices={uniqueOffices}
          updateOfficeBasedOnLoadingPort={updateOfficeBasedOnLoadingPort}
          portList={portList}
        />

        <MilestoneDates
          type={MilestoneType.ESTIMATED}
          title={translate(`estimatedDates`)}
          values={data?.milestones || []}
          options={milestoneDates}
          onUpdate={onUpdateShipmentMilestones}
          viewOnly={viewOnly}
          loading={loadingResources}
          fieldSize={3}
        />

        {/* <MilestoneDates
          type={MilestoneType.ACTUAL}
          title={translate(`actualDates`)}
          options={milestoneDates}
          values={data?.milestones || []}
          onUpdate={onUpdateShipmentMilestones}
          viewOnly={viewOnly}
          loading={loadingResources}
        /> */}
      </Box>
    </div>
  );
};
